<script setup>
/**
 * Campaign creation, in two steps: pick a channel, then pick a template.
 *
 * The channel step is what unified creation behind one button — SMS used to be a
 * footer link and WhatsApp a tab above the grid. A channel-scoped route
 * pre-selects its channel and opens on step two, but only when that channel is
 * actually usable; otherwise the dialog falls back to step one with the reason
 * on the card. That fallback is what lets the page read the first website inbox
 * directly instead of guarding against a missing one.
 *
 * Both steps emit upward: the page owns draft creation, routing, and the
 * per-channel campaign dialogs.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import ChannelStep from './ChannelStep.vue';
import TemplateStep from './TemplateStep.vue';
import { useCampaignTemplates } from 'dashboard/components-next/Campaigns/composables/useCampaignTemplates';
import { TEMPLATE_CHANNELS } from 'dashboard/components-next/Campaigns/helpers/campaignGallery';

const props = defineProps({
  // Channel descriptors from the page, already gated on feature flag and inbox.
  channels: {
    type: Array,
    required: true,
  },
  // The channel the current route scopes to; 'all' (or absent) opens on step one.
  initialChannel: {
    type: String,
    default: '',
  },
  isCreating: {
    type: Boolean,
    default: false,
  },
});

/* eslint-disable vue/no-unused-emit-declarations */
// The per-channel select and scratch events are emitted dynamically through the
// SELECT_EVENTS / SCRATCH_EVENTS maps below, which the rule cannot follow.
const emit = defineEmits([
  'close',
  'selectTemplate',
  'selectSmsTemplate',
  'selectWhatsappTemplate',
  'startFromScratch',
  'createSms',
  'createWhatsapp',
]);
/* eslint-enable vue/no-unused-emit-declarations */

const { t } = useI18n();
// Fetched here rather than in the template step, so switching channels never
// refetches a library that is static seed data anyway.
const { templates, isFetching, fetchTemplates } = useCampaignTemplates();

const selectedChannel = ref(null);

const isAvailable = channel =>
  props.channels.some(
    ({ value, isAvailable: available }) => value === channel && available
  );

const handleSelectChannel = channel => {
  selectedChannel.value = channel;
};

const handleBack = () => {
  selectedChannel.value = null;
};

const title = computed(() =>
  t(
    selectedChannel.value
      ? 'CAMPAIGN.GALLERY.TITLE'
      : 'CAMPAIGN.GALLERY.CHANNEL_STEP.TITLE'
  )
);

const subtitle = computed(() =>
  t(
    selectedChannel.value
      ? 'CAMPAIGN.GALLERY.SUBTITLE'
      : 'CAMPAIGN.GALLERY.CHANNEL_STEP.SUBTITLE'
  )
);

const SELECT_EVENTS = {
  [TEMPLATE_CHANNELS.LIVE_CHAT]: 'selectTemplate',
  [TEMPLATE_CHANNELS.SMS]: 'selectSmsTemplate',
  [TEMPLATE_CHANNELS.WHATSAPP]: 'selectWhatsappTemplate',
};

const SCRATCH_EVENTS = {
  [TEMPLATE_CHANNELS.LIVE_CHAT]: 'startFromScratch',
  [TEMPLATE_CHANNELS.SMS]: 'createSms',
  [TEMPLATE_CHANNELS.WHATSAPP]: 'createWhatsapp',
};

const handleSelectTemplate = template =>
  emit(SELECT_EVENTS[selectedChannel.value], template);

const handleScratch = () => emit(SCRATCH_EVENTS[selectedChannel.value]);

const close = () => emit('close');

const handleBackdropClick = event => {
  if (event.target === event.currentTarget) close();
};

// `aria-modal="true"` hides the rest of the page from assistive tech, so focus
// has to actually move in here and stay here — otherwise the keyboard stays on
// the trigger behind the dialog and tabs through content screen readers can no
// longer see. Focus goes back to the trigger on close.
const dialogRef = ref(null);
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

let previouslyFocused = null;

const handleKeydown = event => {
  if (event.key === 'Escape') {
    close();
    return;
  }
  if (event.key !== 'Tab') return;

  const items = [...(dialogRef.value?.querySelectorAll(FOCUSABLE) ?? [])];
  if (!items.length) return;

  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;

  // Both directions check containment, not just the edge element: picking a
  // channel unmounts ChannelStep along with the focused card, which leaves
  // activeElement on `body`. Tabbing from there would otherwise escape into the
  // content `aria-modal` has already hidden from assistive tech.
  if (
    event.shiftKey &&
    (active === first || !dialogRef.value.contains(active))
  ) {
    event.preventDefault();
    last.focus();
  } else if (
    !event.shiftKey &&
    (active === last || !dialogRef.value.contains(active))
  ) {
    event.preventDefault();
    first.focus();
  }
};

onMounted(async () => {
  if (isAvailable(props.initialChannel)) {
    selectedChannel.value = props.initialChannel;
  }
  previouslyFocused = document.activeElement;
  document.addEventListener('keydown', handleKeydown);
  fetchTemplates();
  await nextTick();
  dialogRef.value?.querySelector(FOCUSABLE)?.focus();
});
onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown);
  previouslyFocused?.focus?.();
});
</script>

<template>
  <TeleportWithDirection to="body">
    <div
      class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-n-alpha-black1 backdrop-blur-[4px]"
      @click="handleBackdropClick"
    >
      <div
        ref="dialogRef"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        class="flex flex-col w-full max-w-3xl min-w-0 border shadow-xl rounded-xl border-n-weak bg-n-alpha-3 backdrop-blur-[100px] h-[85vh] max-h-[40rem] overflow-hidden"
      >
        <div
          class="flex items-start justify-between flex-shrink-0 gap-3 px-6 pt-6 pb-4"
        >
          <div class="flex flex-col gap-1">
            <h3 class="mb-0 text-heading-2 text-n-slate-12">
              {{ title }}
            </h3>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ subtitle }}
            </p>
          </div>
          <Button
            icon="i-lucide-x"
            variant="ghost"
            color="slate"
            size="xs"
            :aria-label="t('GENERAL.CLOSE')"
            @click="close"
          />
        </div>

        <div v-if="selectedChannel" class="flex-shrink-0 px-6 pb-3">
          <Button
            :label="t('CAMPAIGN.GALLERY.CHANNEL_STEP.BACK')"
            variant="ghost"
            color="slate"
            size="xs"
            icon="i-lucide-arrow-left"
            @click="handleBack"
          />
        </div>

        <!-- TemplateStep's own root is `flex flex-1 min-h-0`, so it goes in
             here directly rather than inside another flex wrapper. -->
        <TemplateStep
          v-if="selectedChannel"
          :channel="selectedChannel"
          :templates="templates"
          :is-fetching="isFetching"
          :is-creating="isCreating"
          @select="handleSelectTemplate"
          @scratch="handleScratch"
        />

        <div v-else class="flex-1 min-h-0 px-6 py-2 overflow-y-auto">
          <ChannelStep :channels="channels" @select="handleSelectChannel" />
        </div>
      </div>
    </div>
  </TeleportWithDirection>
</template>
